import React, {useEffect, useRef, useState} from "react";
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  split,
  fromPromise,
} from "@apollo/client";
import {GraphQLWsLink} from "@apollo/client/link/subscriptions";
import {createClient} from "graphql-ws";
import {setContext} from "@apollo/client/link/context";
import {onError} from "@apollo/client/link/error";
import {ApolloLink} from "@apollo/client";
import {RetryLink} from "@apollo/client/link/retry";
import {useFlag} from "@openfeature/react-sdk";
import PropTypes from "prop-types";
import {useAuth} from "../components/authentication/Context/AuthProvider";
import {getMainDefinition} from "@apollo/client/utilities";
import typePolicies from "../graphql/typePolicies";
import {some} from "underscore";

const isLocalDev = process.env.ENVIRONMENT_NAME === "localdev";
const graphqlURL = process.env.GRAPHQL_API;

const GraphQLClientProvider = ({children}) => {
  const {value: enableGraphqlCacheLogging} = useFlag("enableGraphqlCacheLogging", false);
  const enableGraphqlCacheLoggingRef = useRef(enableGraphqlCacheLogging);
  enableGraphqlCacheLoggingRef.current = enableGraphqlCacheLogging;

  const {authSession, refreshSession, logout, setErrorMessage, orgName} = useAuth();
  const [apolloClient, setApolloClient] = useState();
  const authSessionRef = useRef(authSession);
  authSessionRef.current = authSession;
  const refreshSessionRef = useRef(refreshSession);
  refreshSessionRef.current = refreshSession;

  const hasAuth = Boolean(authSession?.AccessToken);

  useEffect(() => {
    if (!hasAuth) {
      return;
    }

    const httpLink = createHttpLink({
      uri: `${graphqlURL}/graphql`,
    });
    const authLink = setContext(async (_, {headers}) => {
      return {
        headers: {
          ...headers,
          orgName: orgName,
          Authorization: `Bearer ${authSessionRef.current?.IdToken}`,
          "X-Client-Platform": "web",
          "X-Authz-Type": "permission",
        },
      };
    });

    const wsLink = new GraphQLWsLink(
      createClient({
        url: `${graphqlURL.replace(/http/, "ws")}/graphql?orgName=${orgName}`,
        connectionParams: () => ({
          authToken: `Bearer ${authSessionRef.current?.IdToken}`,
          authzType: "permission",
        }),
      }),
    );

    const onErrorLink = onError(({graphQLErrors, response, operation, forward, networkError}) => {
      if (
        some(
          graphQLErrors,
          (error) =>
            error.extensions?.exception?.code === "PlatformAccessDenied" ||
            error.extensions?.reason === "PlatformAccessDenied",
        )
      ) {
        console.error("Access denied: PlatformAccessDenied");
        setErrorMessage("Access denied");
        logout();
        return;
      }
      if (
        some(
          graphQLErrors,
          (error) =>
            error.extensions?.exception?.code === "MissingPlatformHeader" ||
            error.extensions?.reason === "MissingPlatformHeader",
        )
      ) {
        console.error("Access denied: Invalid");
        setErrorMessage("Access denied");
        logout();
        return;
      }
      if (networkError?.statusCode === 401) {
        if (operation.getContext().__authRetried) {
          return;
        }
        return fromPromise(
          refreshSessionRef.current().then((result) => result?.IdToken ?? null),
        ).flatMap((token) => {
          if (!token) {
            throw networkError;
          }

          const oldHeaders = operation.getContext().headers;

          operation.setContext({
            headers: {
              ...oldHeaders,
              authorization: `Bearer ${token}`,
            },
            __authRetried: true,
          });

          return forward(operation);
        });
      }
    });

    const retryLink = new RetryLink({
      attempts: (count, operation, error) => {
        const definition = getMainDefinition(operation.query);
        if (definition.operation !== "query") {
          return false;
        }
        return count < 3;
      },
      delay: {
        jitter: true,
        initial: 1500,
      },
    });

    const splitLink = split(
      ({query}) => {
        const definition = getMainDefinition(query);
        return definition.kind === "OperationDefinition" && definition.operation === "subscription";
      },
      wsLink,
      httpLink,
    );

    const cache = new InMemoryCache({typePolicies});
    const logCacheLink = new ApolloLink((operation, forward) => {
      if (!enableGraphqlCacheLoggingRef.current) {
        return forward(operation);
      }

      return forward(operation).map((result) => {
        console.log("Cache snapshot:", cache.extract());
        return result;
      });
    });
    const links = [logCacheLink, onErrorLink, authLink, retryLink, splitLink];

    const link = ApolloLink.from(links);

    const client = new ApolloClient({
      link,
      cache,
      devtools: {
        enabled: isLocalDev,
      },
    });

    setApolloClient(client);
  }, [hasAuth]);

  if (apolloClient) {
    return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
  }

  return null;
};

GraphQLClientProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export {GraphQLClientProvider};
